<template>
  <v-app id="inspire">
    <vue-progress-bar></vue-progress-bar>

    <component
      v-if="isLeftMenuComponent"
      :is="leftMenuComponent"
      @refresh-page="refreshPage($event.timestamp, $event.url)"
    />

    <component
      v-if="isTopMenuComponent"
      :is="topMenuComponent"
      class="smartlink-header"
      @refresh-page="refreshPage($event.timestamp, $event.url)"
    />

    <v-app class="sp-app-class">
      <section :class="contentSection">
        <router-view :key="timestamp" />
      </section>
    </v-app>

    <component v-if="isFooterMenuComponent" :is="footerMenuComponent" />

    <template>
      <system-message
        :sysmsg="sysmsg"
        :sysmsgTitle="sysmsgTitle"
        :sysmsgText="sysmsgText"
        @onConfirmSysmsg="onConfirmSysmsg"
      ></system-message>
      <system-progress
        :sysloading="sysloading"
        :sysloadingTitle="sysloadingTitle"
      ></system-progress>
      <system-alert
        :sysalert="sysalert"
        :sysalertTitle="sysalertTitle"
        :sysalertText="sysalertText"
      ></system-alert>
    </template>
    <dialogWindow
      ref="errorDialog"
      :color="errorDialogColor"
      :info="errorDialogInfo"
      size="max"
      :detail="errorDialogDetail"
      @select-ok="removeDialog()"
    >
    </dialogWindow>
    <dialogWindow
      ref="sessionExpiredDialog"
      color="alert"
      :detail="seDialogInfo"
      @select-ok="logoutDialog()"
    >
    </dialogWindow>
  </v-app>
</template>

<script>
import leftMenu from '@/vue-caas/components/LeftMenu.vue';
import topMenu from '@/vue-caas/components/TopMenu.vue';
import footerMenu from '@/vue-caas/components/FooterMenu.vue';
import config from '@/vue-caas/config/config.js';
import SystemMessage from '@/vue-dlb/components/SystemMessage.vue';
import SystemProgress from '@/vue-dlb/components/SystemProgress.vue';
import SystemAlert from '@/vue-dlb/components/SystemAlert.vue';
import { mapState } from 'vuex';
import { showProgress, hideProgress } from '@/vue-dlb/utils/sys-util.js';
import dialogWindow from '@/vue-dlb/components/NewDialog.vue';

export default {
  name: 'App',

  components: {
    leftMenu,
    topMenu,
    footerMenu,
    SystemProgress,
    SystemMessage,
    SystemAlert,
    dialogWindow,
  },

  data: () => ({
    leftMenuComponent: '',
    topMenuComponent: '',
    footerMenuComponent: '',
    showPopup: false,
    contentSection: 'content-section',
    timestamp: '',
    errorDialogColor: 'error',
    errorDialogInfo: '',
    errorDialogDetail: '',
    seDialogInfo: '',
    thisPageUrl: '',
  }),

  async created() {
    hideProgress();
    this.onConfirmSysmsg();
    // get session infor for embedded page
    if (config.embeddedPages.indexOf(this.$route.path) !== -1 || this.$route.query.auth_token) {
      await this.checkEmbeddedPage();
    }

    try {
      window.$cookies = $cookies;
      // Add a request interceptor
      this.$http.interceptors.request.use(
        async (paramConf) => {
          let checkSession = true;
          this.thisPageUrl = paramConf.url;
          // Execute before send request.
          for (let i = 0; i < config.noSessionCheckApis.length; i += 1) {
            const noSessionCheckApi = config.noSessionCheckApis[i];
            // alert(`paramConf.url: ${paramConf.url}\nnoSessionCheckApi: ${noSessionCheckApi}, i: ${i}`);
            if (paramConf.url.indexOf(noSessionCheckApi) > -1) {
              checkSession = false;
              break;
            }
          }

          if (checkSession) {
            //showProgress();

            try {
              const hash = $cookies.get('hash');
              this.$http.defaults.headers.common['hash'] = hash;
              await this.$http.post(`${config.sessionApiUrl}/session/info`, { hash: hash });
            } catch (err) {
              // Handle request abortion due to popup closing
              if (err.name === 'AbortError' || err.message.includes('aborted')) {
                return;
              }
              // 203728 - CR 23 - Check for new window popup.
              // 20218.7.13 203728 SPKA-153 close new window expired.
              if (window.newWindowExpire) {
                this.$refs.sessionExpiredDialog.dialog = true;
                this.seDialogInfo = 'Session expired.';
                window.opener.location.href = '/login';
              } else if (this.$route.name != 'Login') {
                // this.$router.push('/login?status=logout');
                this.$refs.errorDialog.dialog = false;
                this.$refs.sessionExpiredDialog.dialog = true;
                this.seDialogInfo = 'Session expired.';
              }
            }
          }
          return paramConf;
        },
        (error) => {
          if (!this.$refs.sessionExpiredDialog.dialog) {
            this.errorDialogColor = 'error';
            this.errorDialogInfo = `Request Error`;
            this.errorDialogDetail = `${error.message}`;
            this.$refs.errorDialog.dialog = true;
            return Promise.reject(error);
          }
        },
      );

      this.$http.interceptors.response.use(
        (response) => {
          let checkSession = true;

          // Execute before send request.
          for (let i = 0; i < config.noSessionCheckApis.length; i += 1) {
            const noSessionCheckApi = config.noSessionCheckApis[i];
            // alert(`paramConf.url: ${paramConf.url}\nnoSessionCheckApi: ${noSessionCheckApi}, i: ${i}`);
            if (response.config.url.indexOf(noSessionCheckApi) > -1) {
              checkSession = false;
              break;
            }
          }

          if (checkSession) {
            hideProgress();
          }
          return response;
        },
        (error) => {
          if (this.isOpenResponseDialog()) {
            return Promise.reject(error);
          }
        },
      );
    } catch (err) {
      if (!this.$refs.sessionExpiredDialog.dialog) {
        this.errorDialogColor = 'error';
        this.errorDialogInfo = `Exception occured.`;
        this.errorDialogDetail = `${error.message}`;
        this.$refs.errorDialog.dialog = true;
      }
    }

    try {
      // [App.vue specific] When App.vue is first loaded start the progress bar
      this.$router.beforeEach((to, from, next) => {
        this.setProcess(true);
        next();
      });
      //  hook the progress bar to finish after we've finished moving router-view
      this.$router.afterEach((to, from) => {
        this.checkPage();
      });
    } catch (err) {
      if (!this.$refs.sessionExpiredDialog.dialog) {
        this.errorDialogColor = 'error';
        this.errorDialogInfo = `Exception occured.`;
        this.errorDialogDetail = `${error.message}`;
        this.$refs.errorDialog.dialog = true;
      }
    }

    this.checkPage();
    this.$forceUpdate();
  },

  watch: {},

  computed: {
    isLeftMenuComponent() {
      return this.leftMenuComponent !== '';
    },
    isTopMenuComponent() {
      return this.topMenuComponent !== '';
    },
    isFooterMenuComponent() {
      return this.footerMenuComponent !== '';
    },
    ...mapState('common', {
      sysmsg: 'sysmsg',
      sysmsgTitle: 'sysmsgTitle',
      sysmsgText: 'sysmsgText',
      sysloading: 'sysloading',
      sysloadingTitle: 'sysloadingTitle',
      sysalert: 'sysalert',
      sysalertTitle: 'sysalertTitle',
      sysalertText: 'sysalertText',
    }),
  },

  methods: {
    async checkEmbeddedPage() {
      const hash = this.$route.query.auth_token.replace(/ /g, '+');
      const expiredTime = '12h';
      this.$cookies.set('hash', hash, expiredTime);
      this.$http.defaults.headers.common['hash'] = hash;
      const params = {
        keyCheck: hash,
      };
      await this.$store
        .dispatch('membership/checkAuthToken', params)
        .then((res) => {
          const sessionInfo = res;
          // Cookie expired time unit
          // y: year
          // m: month
          // d: day
          // h: hour
          // min: minute
          // s: second

          this.$cookies.set('user-admin-yn', sessionInfo.sp_flg, expiredTime); // User권한 - Admin 권한을 가진 User인지 여부
          this.$cookies.set('comp-repo-use-yn', sessionInfo.comp_repo_use_flg, expiredTime); // Company권한 - Company용 Repository를 사용하는지 여부
          this.$cookies.set(
            'comp-adminrepo-use-yn',
            sessionInfo.comp_admin_repo_use_flg,
            expiredTime,
          ); // Company권한 - Company가 Admin Repository를 사용하는지 여부
          let comprepo = sessionInfo.comp_repo_use_flg + sessionInfo.comp_admin_repo_use_flg; // ex)YY, YN .. UserRepository사용여부, CLT Repository사용여부
          this.$cookies.set('match-used-repo', comprepo, expiredTime); // Matching을 돌릴때에는 CLT Repository를 사용하는 Customer에 대해서는 접근권한을 준다.  Added for DLB

          //if(sessionInfo.comp_id == cfg.provider && sessionInfo.sp_flg == 'Y') comprepo = sessionInfo.comp_repo_use_flg + 'Y';
          // if(Number(sessionInfo.use_grd_cd) === 9) comprepo = sessionInfo.comp_repo_use_flg + 'Y';
          // else comprepo = sessionInfo.comp_repo_use_flg + 'N';
          // if (sessionInfo.comp_id  == cfg.provider) {
          //   comprepo = sessionInfo.comp_repo_use_flg + 'Y';
          // } else {
          //   comprepo = sessionInfo.comp_repo_use_flg + 'N';
          // }
          // this.$cookies.set('used-repo', comprepo, expiredTime);  // CLT Super Admin이 사용하는 화면들을 위해   Added for DLB

          this.$cookies.set('ownr-id', sessionInfo.ownr_id, expiredTime); // Company정보 - Owner ID
          this.$cookies.set('comp-id', sessionInfo.comp_id, expiredTime); // Company정보 - Company ID
          this.$cookies.set('comp-name', sessionInfo.comp_nm, expiredTime); // Company정보 - Company Name
          this.$cookies.set('user-id', sessionInfo.usr_id, expiredTime); // User정보 - User ID

          this.$cookies.set('user-name', sessionInfo.usr_nm, expiredTime); // User정보 - User ID
          this.$cookies.set('ui-theme', sessionInfo.sel_ui_thm_cd, expiredTime); // User정보 - 선택 테마
          this.$cookies.set('gmt-timezone', sessionInfo.gmt_tmzn_cd, expiredTime); // User정보 - GMT 시간
          this.$cookies.set('date-format', sessionInfo.cnt_dt_fmt_cd, expiredTime); // User정보 - 날짜 포멧
          this.$cookies.set('country-code', sessionInfo.lang_tp_cd, expiredTime); // User정보 - 국가정보 (언어정보)
          this.$cookies.set('use-grade', sessionInfo.use_grd_cd, expiredTime); // User정보 - 사용자 등급

          this.$cookies.set('auth-token', 'Y', expiredTime);
          // alert(`this.$cookies.get('user-admin-yn'): ${this.$cookies.get('user-admin-yn')}
          // this.$cookies.get('comp-repo-use-yn'): ${this.$cookies.get('comp-repo-use-yn')}
          // this.$cookies.get('comp-adminrepo-use-yn'): ${this.$cookies.get('comp-adminrepo-use-yn')}`);

          const authList = [];

          this.$store
            .dispatch('getUser', { usrId: this.$cookies.get('user-id') })
            .then(() => {
              const menuListStr = this.$store.getters.user.menuAuth;
              const userCompId = this.$store.getters.user.compId;
              const menuList = JSON.parse(menuListStr);
              for (let i = 0; i < menuList.length; i += 1) {
                const tmpMenu = menuList[i];
                if (tmpMenu.select !== undefined) {
                  if (tmpMenu.select) {
                    authList.push({
                      h: tmpMenu.href,
                      s: tmpMenu.search,
                      m: tmpMenu.manage,
                    });
                  }
                } else {
                  const tmpParent = JSON.parse(JSON.stringify(tmpMenu));
                  tmpParent.children = [];

                  for (let j = 0; j < tmpMenu.children.length; j += 1) {
                    const tmpChild = tmpMenu.children[j];
                    if (tmpChild.select) {
                      authList.push({
                        h: tmpChild.href,
                        s: tmpChild.search,
                        m: tmpChild.manage,
                      });
                    }
                  }
                  if (tmpChild.children && tmpChild.children.length > 0) {
                    for (let k = 0; k < tmpChild.children.length; k += 1) {
                      const tmpGrandChild = tmpChild.children[k];

                      // level 3
                      if (tmpGrandChild.select) {
                        authList.push({
                          h: tmpGrandChild.href,
                          s: tmpGrandChild.search,
                          m: tmpGrandChild.manage,
                        });
                      }
                    }
                  }
                }
              }

              if (userCompId == config.provider) {
                comprepo = sessionInfo.comp_repo_use_flg + 'Y';
              } else {
                comprepo = sessionInfo.comp_repo_use_flg + 'N';
              }
              this.$cookies.set('used-repo', comprepo, expiredTime); // CLT Super Admin이 사용하는 화면들을 위해   Added for DLB
              this.$cookies.set('menu-auth', JSON.stringify(authList), expiredTime);
              this.$cookies.set('login-user-comp-id', userCompId, expiredTime);
              // alert('success');
              // 2021.3.25 203728 SPKA-57 issue 26
              Store.commit('common/setCompanyForLeftMenu', sessionInfo.comp_id);
              Store.commit('common/isDlb', this.isDlb);
            })
            .catch((error) => {
              if (!this.$refs.sessionExpiredDialog.dialog) {
                this.errorDialogColor = 'error';
                this.errorDialogInfo = `Exception occured.`;
                this.errorDialogDetail = `${error.message}`;
                this.$refs.errorDialog.dialog = true;
              }
            });
        })
        .catch((error) => {
          // Add by April for DLB  2020.06.10
          this.$cookies.remove('user-admin-yn');
          this.$cookies.remove('comp-repo-use-yn');
          this.$cookies.remove('comp-adminrepo-use-yn');
          this.$cookies.remove('comp-id');
          this.$cookies.remove('comp-name');
          this.$cookies.remove('user-id');
          this.$cookies.remove('user-name');
          // this.$cookies.remove('ui-theme');  // User정보 - 선택 테마
          this.$cookies.remove('gmt-timezone'); // User정보 - GMT 시간
          this.$cookies.remove('date-format'); // User정보 - 날짜 포멧
          this.$cookies.remove('country-code'); // User정보 - 국가정보 (언어정보)
          this.$cookies.remove('use-grade'); // User정보 - 사용자 등급
          this.$cookies.remove('menu-auth'); // 메뉴 권한 정보 (json 형식의 파일)
          this.$cookies.remove('auth-token');
          this.$http.defaults.headers.common['hash'] = '';
        });
    },

    checkPage() {
      if (typeof this.$route.meta != 'undefined' && typeof this.$route.meta.popup != 'undefined') {
        this.showPopup = this.$route.meta.popup;
      }

      if (
        config.noMenuPages.indexOf(this.$route.path) !== -1 ||
        this.showPopup ||
        this.$route.path.indexOf('/resetpassword') !== -1 ||
        this.$route.path.indexOf('/verifyemail') !== -1 ||
        this.$route.path.indexOf('/resendemail') !== -1 ||
        this.$route.path.indexOf('/changepassword') !== -1
      ) {
        this.setMenu(false);
        if (this.showPopup) {
          this.contentSection = '';
        }
      } else {
        this.setTheme();
        this.setMenu(true);
        this.setButtonAuth();
      }

      if (
        config.noSessionCheckPages.indexOf(this.$route.path) !== -1 ||
        this.$route.path.indexOf('/resetpassword') !== -1 ||
        this.$route.path.indexOf('/verifyemail') !== -1 ||
        this.$route.path.indexOf('/resendemail') !== -1 ||
        this.$route.path.indexOf('/changepassword') !== -1
      ) {
        this.setProcess(false);
      } else {
        this.checkSession();
      }
    },

    checkSession() {
      const hash = $cookies.get('hash');
      this.$http.defaults.headers.common['hash'] = hash;

      // Check session by session-manager application.
      this.$http
        .post(`${config.sessionApiUrl}/session/info`, { hash: hash })
        .then(() => {
          this.setProcess(false);
        })
        .catch((err) => {
          // Handle request abortion due to popup closing
          if (err.name === 'AbortError' || err.message.includes('aborted')) {
            return;
          }
          // 203728 - CR 23 - Check DLB new window .
          // 20218.7.13 203728 SPKA-153 close new window expired.
          if (window.newWindowExpire) {
            this.$refs.sessionExpiredDialog.dialog = true;
            this.seDialogInfo = 'Session expired.';
            window.opener.location.href = '/login';
          } else if (this.$route.name != 'Login') {
            // this.$router.push('/login?status=logout');
            this.$refs.errorDialog.dialog = false;
            this.$refs.sessionExpiredDialog.dialog = true;
            this.seDialogInfo = 'Session expired.';
          }
          this.setProcess(false);
        });
    },

    setMenu(isSetMenu) {
      if (!isSetMenu) {
        this.leftMenuComponent = '';
        this.topMenuComponent = '';
        this.footerMenuComponent = '';
        this.contentSection = 'login-section';
      } else {
        // 기존에 컴포넌트에 메뉴 셋팅이 되어 있으면, 굳이 다시 셋팅을 할 필요가 없으므로 건너뜀
        if (this.leftMenuComponent === '') {
          this.leftMenuComponent = 'leftMenu';
          this.topMenuComponent = 'topMenu';
          this.footerMenuComponent = 'footerMenu';
          this.contentSection = 'content-section';
        }
      }
    },

    setProcess(isSetProcess) {
      if (isSetProcess) {
        // this.sysloading = true;
        // this.$Progress.start();
      } else {
        // this.sysloading = false;
        // this.$Progress.finish();
      }
    },

    onConfirmSysmsg() {
      Store.commit('common/setSysmsg', false);
    },

    setButtonAuth() {
      try {
        const menuAuth = JSON.parse(this.$cookies.get('menu-auth'));

        for (let i = 0; i < menuAuth.length; i += 1) {
          if (menuAuth[i].h === this.$route.path) {
            this.$auth.search = menuAuth[i].s || false;
            this.$auth.manage = menuAuth[i].m || false;
            // alert(`menuAuth.href: ${menuAuth[i].href}\nthis.$auth.search: ${this.$auth.search}\nthis.$auth.manage: ${this.$auth.manage}`);
            break;
          }
        }
      } catch (err) {
        this.$auth.search = false;
        this.$auth.manage = false;
      }
    },

    setTheme() {
      try {
        if (
          this.$cookies.get('ui-theme') === 'light' &&
          (!this.$vuetify.theme.light || this.$vuetify.theme.light === undefined)
        ) {
          this.$vuetify.theme.dark = false;
          this.$vuetify.theme.light = true;
        } else if (this.$cookies.get('ui-theme') === 'dark' && !this.$vuetify.theme.dark) {
          this.$vuetify.theme.dark = true;
          this.$vuetify.theme.light = false;
        }
      } catch (err) {
        this.$vuetify.theme.dark = true;
        this.$vuetify.theme.light = false;
      }
    },

    refreshPage(timestamp, url) {
      // alert(`timestamp: ${timestamp}\nurl: ${url}`);
      this.timestamp = timestamp;
      this.$router.push(url).catch((err) => {});
    },

    removeDialog() {
      hideProgress();
    },

    logoutDialog() {
      // 20218.7.13 203728 SPKA-153 close new window expired.
      if (window.newWindowExpire) {
        window.close();
      } else {
        hideProgress();
        // this.$router.push('/login?status=logout');
        this.$router.push('/login');
      }
    },

    isOpenResponseDialog() {
      let openDialog = true;
      for (let i = 0; i < config.noResponseAlertApis.length; i += 1) {
        const noSessionCheckApi = config.noResponseAlertApis[i];
        if (this.thisPageUrl.indexOf(noSessionCheckApi) > -1) {
          openDialog = false;
          break;
        }
      }
      return openDialog;
    },
  },
};
</script>

<style>
/*main.v-content {
    padding-top: 40px !important;
  }*/
header.v-toolbar.smartlink-header {
  height: 40px !important;
  left: 45px !important;
}
header.v-toolbar.smartlink-header div.v-toolbar__content {
  height: 40px !important;
}
header.v-toolbar.smartlink-header div.v-toolbar__content div.v-toolbar__title div.container {
  padding-top: 8px !important;
}
button span.v-btn__content {
  text-transform: none;
}
/*.after-login {
    padding-top: 40px;
    padding-bottom: 38px;
    padding-left: 55px;
  }*/
.sp-app-class {
  /* padding-bottom: 40px; */
}
.login-section {
  /* margin-top:auto; */
  margin-bottom: auto;
  margin-left: 0px;
  /* margin-right: 10px; */
}
/* scroll */
/* width */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

/* Track */
::-webkit-scrollbar-track {
  background: #f1f1f1;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: #888;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
